import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import {
  validateInviteToken,
  acceptInvite,
  rejectInviteAction,
  clearCurrentInvite,
  clearActionError
} from '../../redux/slices/inviteSlice';

/**
 * Invite Accept Page
 * 
 * For EXISTING users to accept/reject company invites
 * Phase 4 M2: User Invite System
 */
const InviteAcceptPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { token } = useParams();

  const {
    currentInvite,
    currentInviteLoading,
    currentInviteError,
    actionLoading,
    actionError
  } = useSelector((state) => state.invite);

  const { user: currentUser, isAuthenticated } = useSelector((state) => state.auth);

  const [decision, setDecision] = useState(null); // 'accepted' | 'rejected'

  // Load invite on mount
  useEffect(() => {
    if (token) {
      dispatch(validateInviteToken(token));
    }

    // Cleanup on unmount
    return () => {
      dispatch(clearCurrentInvite());
      dispatch(clearActionError());
    };
  }, [dispatch, token]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (currentInvite && currentInvite.isExistingUser && !isAuthenticated) {
      // Store token in sessionStorage to redirect back after login
      sessionStorage.setItem('invite_token', token);
      history.push(`/login?redirect=/invite/accept/${token}`);
    }
  }, [currentInvite, isAuthenticated, token, history]);

  // Handle accept invite
  const handleAccept = async () => {
    if (!isAuthenticated) {
      history.push(`/login?redirect=/invite/accept/${token}`);
      return;
    }

    try {
      await dispatch(acceptInvite(token)).unwrap();
      setDecision('accepted');

      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        history.push('/dashboard');
        // Reload to update user's company info
        window.location.reload();
      }, 3000);
    } catch (err) {
      console.error('Accept invite error:', err);
    }
  };

  // Handle reject invite
  const handleReject = async () => {
    if (!isAuthenticated) {
      history.push(`/login?redirect=/invite/accept/${token}`);
      return;
    }

    try {
      await dispatch(rejectInviteAction(token)).unwrap();
      setDecision('rejected');

      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        history.push('/dashboard');
      }, 3000);
    } catch (err) {
      console.error('Reject invite error:', err);
    }
  };

  // Loading state
  if (currentInviteLoading) {
    return (
        <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 3 }}>
            Davet bilgileri yükleniyor...
          </Typography>
        </Container>
    );
  }

  // Error state
  if (currentInviteError) {
    return (
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Alert severity="error">
            <Typography variant="h6" gutterBottom>
              Davet Bulunamadı
            </Typography>
            <Typography variant="body2">
              {currentInviteError}
            </Typography>
          </Alert>
          <Button
            variant="contained"
            onClick={() => history.push('/dashboard')}
            sx={{ mt: 3 }}
          >
            Dashboard'a Dön
          </Button>
        </Container>
    );
  }

  // Success state (accepted)
  if (decision === 'accepted') {
    return (
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h4" fontWeight="bold" color="success.main" gutterBottom>
              Davet Kabul Edildi!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Şirkete başarıyla katıldınız. Dashboard'a yönlendiriliyorsunuz...
            </Typography>
            <CircularProgress size={30} />
          </Paper>
        </Container>
    );
  }

  // Success state (rejected)
  if (decision === 'rejected') {
    return (
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CancelIcon color="error" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h4" fontWeight="bold" color="error.main" gutterBottom>
              Davet Reddedildi
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Davet reddedildi. Dashboard'a yönlendiriliyorsunuz...
            </Typography>
            <CircularProgress size={30} />
          </Paper>
        </Container>
    );
  }

  // Main invite view
  if (!currentInvite) {
    return (
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Alert severity="info">
            Davet bilgileri yükleniyor...
          </Alert>
        </Container>
    );
  }

  return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <BusinessIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Şirket Daveti
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Bir şirket sizi şirket üyesi olmaya davet ediyor
          </Typography>
        </Box>

        {/* Error Alert */}
        {actionError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => dispatch(clearActionError())}>
            {actionError}
          </Alert>
        )}

        {/* Invite Details */}
        <Paper sx={{ p: 4, mb: 3 }}>
          {/* Company Info */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="overline" color="text.secondary">
              Şirket
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <BusinessIcon color="primary" />
              <Typography variant="h5" fontWeight="bold">
                {currentInvite.companyId?.companyName || 'Şirket Adı'}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Inviter Info */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="overline" color="text.secondary">
              Davet Eden
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <PersonIcon color="action" />
              <Typography variant="body1">
                {currentInvite.invitedBy?.firstName} {currentInvite.invitedBy?.lastName}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Invited Email */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="overline" color="text.secondary">
              Davet Edilen Email
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <EmailIcon color="action" />
              <Typography variant="body1">
                {currentInvite.email}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Status */}
          <Box>
            <Typography variant="overline" color="text.secondary">
              Davet Durumu
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Chip
                label={currentInvite.status === 'pending' ? 'Beklemede' : currentInvite.status}
                color={currentInvite.status === 'pending' ? 'warning' : 'default'}
                size="small"
              />
            </Box>
          </Box>
        </Paper>

        {/* Info Alert */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Bu daveti kabul ederseniz:
          </Typography>
          <Typography variant="body2" component="div">
            • <strong>{currentInvite.companyId?.companyName}</strong> şirketine katılacaksınız
            <br />
            • Şirket adına belgeler oluşturabilir ve yönetebilirsiniz
            <br />
            • Mevcut kullanıcı tipiniz <strong>şirket üyesi</strong> olarak güncellenecek
          </Typography>
        </Alert>

        {/* Action Buttons */}
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              color="error"
              size="large"
              startIcon={<CancelIcon />}
              onClick={handleReject}
              disabled={actionLoading}
              sx={{ minWidth: 150 }}
            >
              Reddet
            </Button>
            <Button
              variant="contained"
              color="success"
              size="large"
              startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
              onClick={handleAccept}
              disabled={actionLoading}
              sx={{ minWidth: 150 }}
            >
              {actionLoading ? 'İşleniyor...' : 'Kabul Et'}
            </Button>
          </Box>
        </Paper>

        {/* Warning */}
        {currentUser && currentUser.email !== currentInvite.email && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            ⚠️ Bu davet <strong>{currentInvite.email}</strong> adresine gönderilmiş. 
            Şu anda <strong>{currentUser.email}</strong> ile giriş yaptınız. 
            Daveti kabul etmek için doğru hesap ile giriş yapmanız gerekiyor.
          </Alert>
        )}
      </Container>
  );
};

export default InviteAcceptPage;

